import { LoopContext } from '../types'

export interface LoopConditionPanelOptions {
  container: HTMLElement
}

export class LoopConditionPanel {
  private container: HTMLElement
  private options: LoopConditionPanelOptions
  private currentLoopStack: LoopContext[] = []
  private loopHistory: LoopContext[] = [] // すべてのループ履歴を保持
  
  constructor(options: LoopConditionPanelOptions) {
    this.options = options
    this.container = options.container
    this.setupStyles()
    this.render()
  }
  
  /**
   * ループ状態を更新
   */
  updateLoopStack(loopStack: LoopContext[]): void {
    this.currentLoopStack = loopStack
    
    // ループスタックが空でない場合、履歴に追加
    if (loopStack.length > 0) {
      // 最も内側のループ（最後の要素）を履歴に追加
      const innermostLoop = loopStack[loopStack.length - 1]
      
      // 同じループの同じイテレーションが既に履歴にあるかチェック
      const existingIndex = this.loopHistory.findIndex(h => 
        h.type === innermostLoop.type && 
        h.level === innermostLoop.level && 
        h.iteration === innermostLoop.iteration
      )
      
      if (existingIndex === -1) {
        // 新規追加
        this.loopHistory.push(JSON.parse(JSON.stringify(innermostLoop)))
      } else {
        // 既存のエントリを更新（条件結果などが変更された場合）
        this.loopHistory[existingIndex].conditionResult = innermostLoop.conditionResult
        this.loopHistory[existingIndex].evaluatedCondition = innermostLoop.evaluatedCondition
        // variablesAfterがある場合は更新
        if (innermostLoop.variablesAfter) {
          this.loopHistory[existingIndex].variablesAfter = JSON.parse(JSON.stringify(innermostLoop.variablesAfter))
        }
      }
    }
    
    this.render()
  }
  
  /**
   * 履歴をクリア
   */
  clearHistory(): void {
    this.loopHistory = []
    this.currentLoopStack = []
    this.render()
  }
  
  /**
   * スタイルを設定
   */
  private setupStyles(): void {
    const style = document.createElement('style')
    style.textContent = `
      .loop-condition-panel {
        height: 100%;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 0.75rem;
        scrollbar-width: thin;
        scrollbar-color: #e5e7eb transparent;
      }
      
      .loop-condition-panel::-webkit-scrollbar {
        width: 6px;
      }
      
      .loop-condition-panel::-webkit-scrollbar-track {
        background: transparent;
      }
      
      .loop-condition-panel::-webkit-scrollbar-thumb {
        background-color: #e5e7eb;
        border-radius: 3px;
      }
      
      .loop-condition-panel::-webkit-scrollbar-thumb:hover {
        background-color: #d1d5db;
      }
      
      .loop-condition-item {
        margin-bottom: 0.75rem;
        background-color: var(--surface-color);
        border: 1px solid var(--border-color);
        border-radius: 0.375rem;
        overflow: hidden;
        transition: all 0.3s ease;
        min-height: 120px;
        flex-shrink: 0;
        animation: conditionFade 0.3s ease-out;
      }
      
      .loop-condition-item--active {
        border-color: var(--primary-color);
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
      }
      
      .loop-condition-header {
        padding: 0.5rem 0.75rem;
        background-color: #f9fafb;
        border-bottom: 1px solid var(--border-color);
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        min-height: 36px;
      }
      
      .loop-condition-item--active .loop-condition-header {
        background-color: rgba(37, 99, 235, 0.05);
      }
      
      .loop-type {
        font-weight: 600;
        color: var(--primary-color);
        text-transform: uppercase;
        font-size: 0.75rem;
      }
      
      .loop-level {
        font-size: 0.75rem;
        color: var(--text-secondary);
        background-color: #e5e7eb;
        padding: 0.125rem 0.375rem;
        border-radius: 0.25rem;
      }
      
      .loop-iteration {
        margin-left: auto;
        font-size: 0.875rem;
        color: var(--text-secondary);
      }
      
      .loop-condition-body {
        padding: 0.75rem;
        min-height: 84px;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      
      .condition-expression {
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 0.875rem;
        background-color: #f9fafb;
        padding: 0.625rem 0.875rem;
        border-radius: 0.375rem;
        margin-bottom: 0.5rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
        min-height: 36px;
      }
      
      .condition-text {
        color: var(--text-primary);
        font-weight: 500;
      }
      
      .condition-result {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      
      .condition-result-value {
        font-weight: 600;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
      }
      
      .condition-result-value--true {
        color: var(--secondary-color);
        background-color: rgba(16, 185, 129, 0.1);
      }
      
      .condition-result-value--false {
        color: var(--error-color);
        background-color: rgba(239, 68, 68, 0.1);
      }
      
      .condition-arrow {
        font-size: 1.25rem;
      }
      
      .condition-evaluation {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }
      
      .evaluation-step {
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 0.75rem;
        color: var(--text-secondary);
        padding-left: 1rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      
      .evaluation-step::before {
        content: "→";
        color: var(--primary-color);
      }
      
      .loop-variables {
        margin-top: 0.5rem;
        padding-top: 0.5rem;
        border-top: 1px solid var(--border-color);
        min-height: 32px;
      }
      
      .loop-variables:first-of-type {
        margin-top: 0.75rem;
      }
      
      .loop-variables + .loop-variables {
        margin-top: 0.75rem;
        padding-top: 0.75rem;
        border-top: 1px dashed var(--border-color);
      }
      
      .loop-variables-title {
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--text-secondary);
        margin-bottom: 0.5rem;
      }
      
      .loop-variable-list {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
      }
      
      .loop-variable {
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 0.75rem;
        background-color: #f3f4f6;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }
      
      .loop-variable-name {
        color: var(--text-primary);
        font-weight: 500;
      }
      
      .loop-variable-value {
        color: var(--primary-color);
        font-weight: 600;
      }
      
      .empty-state {
        text-align: center;
        color: var(--text-secondary);
        padding: 2rem;
        font-size: 0.875rem;
        min-height: 200px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      
      .empty-state__icon {
        font-size: 2rem;
        margin-bottom: 0.5rem;
        opacity: 0.5;
      }
      
      .loop-action {
        margin-top: auto;
        padding: 0.5rem;
        background-color: #f9fafb;
        border-radius: 0.375rem;
        font-size: 0.8125rem;
        color: var(--text-secondary);
        text-align: center;
        min-height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .loop-action--continue {
        color: var(--secondary-color);
        background-color: rgba(16, 185, 129, 0.05);
      }
      
      .loop-action--exit {
        color: var(--error-color);
        background-color: rgba(239, 68, 68, 0.05);
      }
    `
    document.head.appendChild(style)
  }
  
  /**
   * レンダリング
   */
  private render(): void {
    if (this.loopHistory.length === 0 && this.currentLoopStack.length === 0) {
      this.container.innerHTML = `
        <div class="loop-condition-panel">
          <div class="empty-state">
            <div class="empty-state__icon">🔄</div>
            <div>ループが実行されていません</div>
          </div>
        </div>
      `
      return
    }
    
    let html = '<div class="loop-condition-panel">'
    
    // 履歴を表示（新しいものが上に来るように逆順で）
    const reversedHistory = [...this.loopHistory].reverse()
    
    reversedHistory.forEach((loop, index) => {
      // 最新のエントリがアクティブ
      const isActive = index === 0 && this.currentLoopStack.length > 0
      html += this.renderLoopCondition(loop, isActive)
    })
    
    html += '</div>'
    this.container.innerHTML = html
  }
  
  /**
   * ループ条件をレンダリング
   */
  private renderLoopCondition(loop: LoopContext, isActive: boolean): string {
    const conditionParts = this.parseCondition(loop.condition)
    const resultText = loop.conditionResult ? 'true' : 'false'
    const resultClass = loop.conditionResult ? 'condition-result-value--true' : 'condition-result-value--false'
    const action = loop.conditionResult ? '継続' : '終了'
    const actionClass = loop.conditionResult ? 'loop-action--continue' : 'loop-action--exit'
    
    return `
      <div class="loop-condition-item ${isActive ? 'loop-condition-item--active' : ''}">
        <div class="loop-condition-header">
          <span class="loop-type">${loop.type}</span>
          <span class="loop-level">レベル ${loop.level + 1}</span>
          <span class="loop-iteration">イテレーション: ${loop.iteration + 1}</span>
        </div>
        <div class="loop-condition-body">
          <div class="condition-expression">
            <span class="condition-text">${loop.condition}</span>
            <div class="condition-result">
              <span class="condition-arrow">→</span>
              <span class="condition-result-value ${resultClass}">${resultText}</span>
            </div>
          </div>
          
          ${conditionParts ? `
            <div class="condition-evaluation">
              ${conditionParts.map(part => `
                <div class="evaluation-step">${part}</div>
              `).join('')}
            </div>
          ` : ''}
          
          ${loop.variables && loop.variables.length > 0 ? `
            <div class="loop-variables">
              <div class="loop-variables-title">開始時:</div>
              <div class="loop-variable-list">
                ${loop.variables.map(v => `
                  <div class="loop-variable">
                    <span class="loop-variable-name">${v.name}:</span>
                    <span class="loop-variable-value">${v.value}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}
          
          ${loop.variablesAfter && loop.variablesAfter.length > 0 ? `
            <div class="loop-variables">
              <div class="loop-variables-title">終了時:</div>
              <div class="loop-variable-list">
                ${loop.variablesAfter.map(v => `
                  <div class="loop-variable">
                    <span class="loop-variable-name">${v.name}:</span>
                    <span class="loop-variable-value">${v.value}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}
          
          <div class="loop-action ${actionClass}">
            ループを${action}します
          </div>
        </div>
      </div>
    `
  }
  
  /**
   * 条件式を解析して評価ステップを生成
   */
  private parseCondition(condition: string): string[] | null {
    // 簡単な条件式の解析（例: i < 5）
    const simpleComparison = condition.match(/(\w+)\s*([<>]=?|==|!=)\s*(\w+)/)
    if (simpleComparison) {
      const [, left, operator, right] = simpleComparison
      const steps = []
      
      // 変数の値を含むステップを追加
      const leftVar = this.findVariable(left)
      const rightVar = this.findVariable(right)
      
      if (leftVar) {
        steps.push(`${left} = ${leftVar.value}`)
      }
      if (rightVar && isNaN(Number(right))) {
        steps.push(`${right} = ${rightVar.value}`)
      }
      
      // 評価式
      const leftValue = leftVar ? leftVar.value : left
      const rightValue = rightVar ? rightVar.value : right
      steps.push(`${leftValue} ${operator} ${rightValue}`)
      
      return steps
    }
    
    return null
  }
  
  /**
   * 変数を検索
   */
  private findVariable(name: string): any {
    for (const loop of this.currentLoopStack) {
      if (loop.variables) {
        const variable = loop.variables.find(v => v.name === name)
        if (variable) return variable
      }
    }
    return null
  }
  
  /**
   * ループ関連の変数かどうか判定
   */
  private isLoopRelatedVariable(name: string): boolean {
    return ['i', 'j', 'k', 'n', 'count', 'index'].includes(name) ||
           (name.length === 1 && name >= 'i' && name <= 'n')
  }
  
  /**
   * クリーンアップ
   */
  dispose(): void {
    this.container.innerHTML = ''
  }
}